/**
 * Stakeholder Validation Schemas
 */

import { z } from 'zod';
import { nameField, optionalBoundedTextOrEmpty } from './shared';

/**
 * Common validation rules for Stakeholder entities
 */
export const stakeholderValidationRules = {
  /** Stakeholder name validation (1-200 characters) */
  name: nameField(
    200,
    'Stakeholder name is required',
    'Stakeholder name must be less than 200 characters'
  ),

  /** Description validation (optional, max 2000 characters) */
  description: optionalBoundedTextOrEmpty(2000, 'Description must be less than 2000 characters'),

  /** Contact email validation (optional) */
  contactEmail: z.union([z.string().email('Invalid email address'), z.literal('')]).optional(),

  /** Organization validation (optional string) */
  organization: optionalBoundedTextOrEmpty(200, 'Organization must be less than 200 characters'),
};

/**
 * Full Stakeholder create form schema
 */
export const createStakeholderSchema = z.object({
  name: stakeholderValidationRules.name,
  stakeholderType: z.enum(['PartnerAgencyOwner', 'PartnerOwner'], {
    message: 'Stakeholder type is required',
  }),
  organization: stakeholderValidationRules.organization,
  description: stakeholderValidationRules.description,
  contactEmail: stakeholderValidationRules.contactEmail,
});

/**
 * Stakeholder update form schema (all fields optional except at least one must be present)
 */
export const updateStakeholderSchema = z
  .object({
    name: stakeholderValidationRules.name.optional(),
    stakeholderType: z.enum(['PartnerAgencyOwner', 'PartnerOwner']).optional(),
    organization: stakeholderValidationRules.organization,
    description: stakeholderValidationRules.description,
    contactEmail: stakeholderValidationRules.contactEmail,
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

/**
 * Type inference for create stakeholder form
 */
export type CreateStakeholderFormData = z.infer<typeof createStakeholderSchema>;

/**
 * Type inference for update stakeholder form
 */
export type UpdateStakeholderFormData = z.infer<typeof updateStakeholderSchema>;
